// Reading one shared note, for somebody who is not signed in and never will be.
//
// The smallest hook in this client, and every difference from the others is the
// same difference: THERE IS NO SESSION HERE AND THERE IS NO SHELL BEHIND IT.
//
//   NO `onSignedOut`. Every other read in this client raises the sign-in gate on
//   a 401 (`useDayScreen`'s `signedOut`). This one cannot: a stranger holding a
//   link has no account to be signed out of, and offering them a Google button
//   would be answering "this link is dead" with "become a user". The endpoint
//   does not answer 401 in any case - it answers 404, like everything else that
//   is not a live link.
//
//   NO `refresh` AND NO REVISION. Nothing on this page writes, so there is
//   nothing that could make what is on screen stale from this side. If the owner
//   revokes the link while somebody is reading, that person keeps the words on
//   their screen and gets nothing on the next load - which is what a read-only
//   link means and is not something to paper over with polling.
//
//   THREE STATES AND NOT FOUR. Loading, the note, and one failure - because a
//   revoked token, a wrong token, a link to a deleted note and a malformed one
//   all arrive as the same 404 and must stay indistinguishable (a page that
//   ranked them would be a way to ask which guess was closer). A network failure
//   is the one thing told apart, because it is the only one where trying again
//   can end differently.

import { useCallback, useEffect, useState } from 'react';
import { api, ApiClientError, messageFor } from '../api/client.ts';
import type { SharedNote } from '../api/client.ts';

export type SharedNoteState =
  | { kind: 'loading' }
  | { kind: 'note'; note: SharedNote }
  /** The link does not work, and this page will never know which way. */
  | { kind: 'gone' }
  /** Something went wrong on the way. The only state with a Try again. */
  | { kind: 'failed'; problem: string };

export function useSharedNote(token: string): {
  state: SharedNoteState;
  retry: () => void;
} {
  const [state, setState] = useState<SharedNoteState>({ kind: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const note = await api.sharedNote(token);
        if (live) setState({ kind: 'note', note });
      } catch (error) {
        if (!live) return;
        // 404 IS THE ORDINARY ANSWER HERE, not a failure to report. It is what a
        // revoked link, a wrong link and a link to a deleted note all give, and
        // it is the one state this page has words of its own for.
        if (error instanceof ApiClientError && error.status === 404) {
          setState({ kind: 'gone' });
          return;
        }
        setState({ kind: 'failed', problem: messageFor(error) });
      }
    })();
    return () => {
      live = false;
    };
  }, [token, attempt]);

  const retry = useCallback(() => {
    setState({ kind: 'loading' });
    setAttempt((n) => n + 1);
  }, []);

  return { state, retry };
}
